import { createApi } from '@reduxjs/toolkit/query/react';
import { RestClientWrapper } from '@/core/api/restClientWrapper';
import { NotificationStore } from '@wunder-mobility/ui-components';
import { t } from '@lingui/core/macro';
import { SubscriptionCacheTags } from '../enums';
import {
  Subscription,
  SubscriptionListResponse,
  BuySubscriptionArgs,
  CancelSubscriptionArgs,
  ReactivateSubscriptionArgs,
  RenewSubscriptionArgs,
  SwitchSubscriptionArgs,
  CancelSwitchArgs,
} from '../types/Subscription.types';

export const SubscriptionService = createApi({
  reducerPath: 'api-subscriptions',
  baseQuery: RestClientWrapper,
  tagTypes: [SubscriptionCacheTags.SUBSCRIPTIONS, SubscriptionCacheTags.SUBSCRIPTION_DETAIL],
  endpoints: (builder) => ({
    // Get available subscriptions for a branch (catalog)
    requestAvailableSubscriptions: builder.query<SubscriptionListResponse, { branchId: number }>({
      query: ({ branchId }) => ({
        url: '/front/subscriptions',
        requestParams: {
          params: { branchId },
        },
      }),
      providesTags: [SubscriptionCacheTags.SUBSCRIPTIONS],
    }),

    // Get user's owned subscriptions
    requestMySubscriptions: builder.query<SubscriptionListResponse, void>({
      query: () => ({
        url: '/front/subscriptions',
        // No params - this endpoint returns user's subscriptions
      }),
      providesTags: [SubscriptionCacheTags.SUBSCRIPTIONS],
      transformResponse: (response: SubscriptionListResponse): SubscriptionListResponse => {
        // The API returns an array of objects with nested structure:
        // [{ subscription: {...}, boughtAt: "...", validUntil: "...", autoRenewal: "..." }]
        // We need to flatten it to match the Subscription type
        if (!Array.isArray(response)) {
          return [];
        }
        return response.map((item: any) => {
          // Merge subscription data with user-specific fields
          // Explicitly preserve details if it exists
          return {
            ...item.subscription,
            details: item.subscription?.details,
            // User-subscription wrapper fields (needed for switching, see BUS-2399)
            id: item.id,
            state: item.state,
            switchingTarget: item.switchingTarget,
            canceled: item.canceled,
            boughtAt: item.boughtAt,
            validUntil: item.validUntil,
            autoRenewal: item.autoRenewal,
            nextBillingDate: item.nextBillingDate,
            paymentMethod: item.paymentMethod,
            canChangePaymentMethod: item.canChangePaymentMethod,
          };
        });
      },
    }),

    requestSubscriptionDetails: builder.query<Subscription, string>({
      query: (subscriptionCode) => ({
        url: `/front/subscriptions/${subscriptionCode}`,
      }),
      providesTags: (result, error, subscriptionCode) => [
        { type: SubscriptionCacheTags.SUBSCRIPTION_DETAIL, id: subscriptionCode },
      ],
    }),

    requestBuySubscription: builder.mutation<void, BuySubscriptionArgs>({
      query: ({ subscriptionCode }) => ({
        url: `/front/subscriptions/buy/${subscriptionCode}`,
        requestParams: { method: 'post' },
        config: {
          onRequestSuccess: () => {
            NotificationStore.addNotification({
              type: 'success',
              title: t({ id: 'notifications.general.success', message: 'Success' }),
              content: t({
                id: 'subscriptions.purchase.success',
                message: 'Plan is now active.',
              }),
              duration: 5000,
              position: 'bottom-right',
            });
          },
        },
      }),
      invalidatesTags: [
        SubscriptionCacheTags.SUBSCRIPTIONS,
        SubscriptionCacheTags.SUBSCRIPTION_DETAIL,
      ],
    }),

    requestCancelSubscription: builder.mutation<{ validUntil: string }, CancelSubscriptionArgs>({
      query: ({ subscriptionCode }) => ({
        url: `/front/subscriptions/cancel/${subscriptionCode}`,
        requestParams: { method: 'post' },
        config: {
          onRequestSuccess: () => {
            NotificationStore.addNotification({
              type: 'info',
              title: t({
                id: 'subscriptions.cancel.title',
                message: 'Subscription Updated',
              }),
              content: t({
                id: 'subscriptions.cancel.success',
                message: 'Cancellation scheduled.',
              }),
              duration: 5000,
              position: 'bottom-right',
            });
          },
        },
      }),
      invalidatesTags: [
        SubscriptionCacheTags.SUBSCRIPTIONS,
        SubscriptionCacheTags.SUBSCRIPTION_DETAIL,
      ],
    }),

    requestReactivateSubscription: builder.mutation<void, ReactivateSubscriptionArgs>({
      query: ({ subscriptionCode }) => ({
        url: `/front/subscriptions/reactivate/${subscriptionCode}`,
        requestParams: { method: 'post' },
        config: {
          onRequestSuccess: () => {
            NotificationStore.addNotification({
              type: 'success',
              title: t({ id: 'notifications.general.success', message: 'Success' }),
              content: t({
                id: 'subscriptions.reactivate.success',
                message: 'Subscription successfully reactivated.',
              }),
              duration: 5000,
              position: 'bottom-right',
            });
          },
        },
      }),
      invalidatesTags: [
        SubscriptionCacheTags.SUBSCRIPTIONS,
        SubscriptionCacheTags.SUBSCRIPTION_DETAIL,
      ],
    }),

    requestRenewSubscription: builder.mutation<void, RenewSubscriptionArgs>({
      query: ({ subscriptionCode }) => ({
        url: `/front/subscriptions/renew/${subscriptionCode}`,
        requestParams: { method: 'post' },
        config: {
          onRequestSuccess: () => {
            NotificationStore.addNotification({
              type: 'success',
              title: t({ id: 'notifications.general.success', message: 'Success' }),
              content: t({
                id: 'subscriptions.renew.success',
                message: 'Subscription renewed successfully.',
              }),
              duration: 5000,
              position: 'bottom-right',
            });
          },
        },
      }),
      invalidatesTags: [
        SubscriptionCacheTags.SUBSCRIPTIONS,
        SubscriptionCacheTags.SUBSCRIPTION_DETAIL,
      ],
    }),

    // Get a single user-subscription by id (current state + switchingTarget)
    requestUserSubscriptionDetails: builder.query<Subscription, number>({
      query: (id) => ({
        url: `/front/user-subscriptions/${id}`,
      }),
      transformResponse: (item: any): Subscription => ({
        ...item.subscription,
        details: item.subscription?.details,
        id: item.id,
        state: item.state,
        switchingTarget: item.switchingTarget,
        canceled: item.canceled,
        boughtAt: item.boughtAt,
        validUntil: item.validUntil,
        autoRenewal: item.autoRenewal,
        nextBillingDate: item.nextBillingDate,
        paymentMethod: item.paymentMethod,
        canChangePaymentMethod: item.canChangePaymentMethod,
      }),
      providesTags: (result, error, id) => [
        { type: SubscriptionCacheTags.SUBSCRIPTION_DETAIL, id },
      ],
    }),

    // Switch the active subscription to another tier (immediately or at next renewal)
    requestSwitchSubscription: builder.mutation<void, SwitchSubscriptionArgs>({
      query: ({ id, targetSubscriptionCode, switchImmediately }) => ({
        url: `/front/user-subscriptions/${id}/switch`,
        requestParams: {
          method: 'post',
          data: { targetSubscriptionCode, switchImmediately },
        },
        config: {
          onRequestSuccess: () => {
            NotificationStore.addNotification({
              type: 'success',
              title: t({ id: 'notifications.general.success', message: 'Success' }),
              content: t({
                id: 'subscriptions.switch.success',
                message: 'Subscription switch confirmed.',
              }),
              duration: 5000,
              position: 'bottom-right',
            });
          },
        },
      }),
      invalidatesTags: [
        SubscriptionCacheTags.SUBSCRIPTIONS,
        SubscriptionCacheTags.SUBSCRIPTION_DETAIL,
      ],
    }),

    // Cancel a pending switch while in SWITCHING state (reverts to RENEWING)
    requestCancelSwitch: builder.mutation<void, CancelSwitchArgs>({
      query: ({ id }) => ({
        url: `/front/user-subscriptions/${id}`,
        requestParams: { method: 'delete' },
        config: {
          onRequestSuccess: () => {
            NotificationStore.addNotification({
              type: 'info',
              title: t({
                id: 'subscriptions.cancelSwitch.title',
                message: 'Subscription Updated',
              }),
              content: t({
                id: 'subscriptions.cancelSwitch.success',
                message: 'Pending switch cancelled.',
              }),
              duration: 5000,
              position: 'bottom-right',
            });
          },
        },
      }),
      invalidatesTags: [
        SubscriptionCacheTags.SUBSCRIPTIONS,
        SubscriptionCacheTags.SUBSCRIPTION_DETAIL,
      ],
    }),
  }),
});

export const {
  useRequestAvailableSubscriptionsQuery,
  useRequestMySubscriptionsQuery,
  useRequestSubscriptionDetailsQuery,
  useRequestBuySubscriptionMutation,
  useRequestCancelSubscriptionMutation,
  useRequestReactivateSubscriptionMutation,
  useRequestRenewSubscriptionMutation,
  useRequestUserSubscriptionDetailsQuery,
  useRequestSwitchSubscriptionMutation,
  useRequestCancelSwitchMutation,
} = SubscriptionService;
